import { type ThunkAction, type ThunkDispatch } from "@reduxjs/toolkit";
import type { Unsubscribe } from "firebase/firestore";

import { type Menu, type MenuSection } from "@dashboard/types/menu";
import { type ReduxState } from "@dashboard/types/reduxState";
import { type ThunkExtraArgument } from "@dashboard/types/thunk";

import { trackDashboardEvent } from "../../mixpanel/mixpanel";
import { type ErrorReducerAction } from "../reducers/errorReducer";
import { type MenuReducerAction } from "../reducers/menuReducer";
import { addError } from "./addErrorAction";
import { applyUrlAssignmentDelta } from "./menuUrlAssignmentActions";

export type MenuSaveResult = { ok: true } | { ok: false; error: unknown };

type ThunkActionReturn = ThunkAction<
  Promise<void>,
  ReduxState,
  ThunkExtraArgument,
  MenuReducerAction | ErrorReducerAction
>;

type MenuSaveThunkReturn = ThunkAction<
  Promise<MenuSaveResult>,
  ReduxState,
  ThunkExtraArgument,
  MenuReducerAction | ErrorReducerAction
>;

export type MenuThunkDispatch = ThunkDispatch<
  ReduxState,
  ThunkExtraArgument,
  MenuReducerAction | ErrorReducerAction
>;

const setMenu: (
  menu: Menu,
  creatingNew: boolean,
  menuSectionsOfMenu?: MenuSection[]
) => MenuSaveThunkReturn = (
  menu: Menu,
  creatingNew: boolean,
  menuSectionsOfMenu?: MenuSection[]
) => {
  return async (
    dispatch: MenuThunkDispatch,
    getState: () => ReduxState,
    { menuApi }: ThunkExtraArgument
  ): Promise<MenuSaveResult> => {
    const previousMenuIdsInDisplayOrder = getState().menu.menuIdsInDisplayOrder;
    const wasViewable = getState().menu.menus.some(
      (stored) => stored.id === menu.id && stored.isViewable
    );
    try {
      await menuApi.setMenu(
        menu,
        creatingNew,
        previousMenuIdsInDisplayOrder,
        menuSectionsOfMenu
      );
      dispatch({ type: "SET_MENU", payload: { menu, menuSectionsOfMenu } });
      if (menu.isViewable && !wasViewable) {
        trackDashboardEvent("Menu Published", { menu_id: menu.id });
      }
      return { ok: true };
    } catch (error) {
      addError(dispatch)(error as Error | string);
      return { ok: false, error };
    }
  };
};

export const toggleMenuStatus: (menu: Menu) => MenuSaveThunkReturn = (
  menu: Menu
) => updateMenu({ ...menu, isViewable: !menu.isViewable } as Menu);

export const updateMenu: (
  menu: Menu,
  menuSectionsOfMenu?: MenuSection[]
) => MenuSaveThunkReturn = (menu: Menu, menuSectionsOfMenu?: MenuSection[]) =>
  setMenu(menu, false, menuSectionsOfMenu);

export const createMenu: (
  menu: Menu,
  menuSectionsOfMenu: MenuSection[]
) => MenuSaveThunkReturn = (menu: Menu, menuSectionsOfMenu?: MenuSection[]) =>
  setMenu(menu, true, menuSectionsOfMenu);

export const setMenuDisplayOrder: (
  menuIdsInDisplayOrder: string[]
) => ThunkActionReturn = (menuIdsInDisplayOrder: string[]) => {
  return (
    dispatch: MenuThunkDispatch,
    getState: () => ReduxState,
    { menuApi }: ThunkExtraArgument
  ) => {
    const restaurantUuid = getState().restaurant.selectedRestaurant!.uuid;
    return menuApi
      .setMenuDisplayOrder(restaurantUuid, menuIdsInDisplayOrder)
      .then(() => {
        dispatch({
          type: "SET_MENU_DISPLAY_ORDER",
          payload: menuIdsInDisplayOrder,
        });
      })
      .catch(addError(dispatch));
  };
};

// Live listener: returns the unsubscribe synchronously (not a promise).
export const subscribeToMenus =
  (
    restaurantUuid: string
  ): ThunkAction<
    Unsubscribe,
    ReduxState,
    ThunkExtraArgument,
    MenuReducerAction
  > =>
  (dispatch, _getState, { menuApi }) =>
    menuApi.subscribeToMenus(
      restaurantUuid,
      (payload) => dispatch({ type: "FETCHED_MENUS", payload }),
      addError(dispatch)
    );

export const deleteMenu: (
  restaurantUuid: string,
  menuId: string
) => ThunkActionReturn = (restaurantUuid: string, menuId: string) => {
  return (
    dispatch: MenuThunkDispatch,
    getState: () => ReduxState,
    { menuApi }: ThunkExtraArgument
  ) => {
    const previousMenuIdsInDisplayOrder = getState().menu.menuIdsInDisplayOrder;
    const menuSectionIdsToDelete = getState().menu.menuSectionsByMenuId[
      menuId
    ].map((menuSection) => menuSection.id);

    // Menu URLs (v2) keep their own explicit menuIdsInDisplayOrder, separate
    // from the restaurant-level order. Remove the menu from every URL that
    // references it BEFORE deleting the menu: if a URL update fails we abort and
    // leave the menu in place, rather than deleting it and leaving a URL
    // pointing at a menu that no longer exists.
    const urlsReferencingMenu = getState().menuUrlV2.menuUrlsV2.filter((url) =>
      url.menuIdsInDisplayOrder.includes(menuId)
    );

    return Promise.all(
      urlsReferencingMenu.map((url) =>
        dispatch(applyUrlAssignmentDelta(url.docId, menuId, "remove"))
      )
    )
      .then((results) => {
        const failure = results.find(
          (result): result is { ok: false; error: unknown } => !result.ok
        );
        if (failure) {
          throw failure.error;
        }
        return menuApi.deleteMenu(
          restaurantUuid,
          menuId,
          previousMenuIdsInDisplayOrder,
          menuSectionIdsToDelete
        );
      })
      .then(() => {
        dispatch({ type: "DELETE_MENU", payload: menuId });
      })
      .catch(addError(dispatch));
  };
};

export const setMenuSection: (menuSection: MenuSection) => ThunkActionReturn = (
  menuSection: MenuSection
) => {
  return (
    dispatch: MenuThunkDispatch,
    getState: () => ReduxState,
    { menuApi }: ThunkExtraArgument
  ) => {
    return menuApi
      .setMenuSection(menuSection)
      .then(() => {
        dispatch({ type: "SET_MENU_SECTION", payload: menuSection });
      })
      .catch(addError(dispatch));
  };
};

// Writes and reports whether the write landed, unlike setMenuSection above
// which resolves either way. The menu tree renders from local state so a
// drag feels immediate, which means it has to put that state back when the
// write fails; without a signal it would go on showing an edit the database
// never took. The boolean exists for that revert alone. The error toast is
// still dispatched here, so a caller must not raise a second one.
export const setMenuSectionReportingResult: (
  menuSection: MenuSection
) => ThunkAction<
  Promise<boolean>,
  ReduxState,
  ThunkExtraArgument,
  MenuReducerAction | ErrorReducerAction
> = (menuSection: MenuSection) => {
  return (
    dispatch: MenuThunkDispatch,
    getState: () => ReduxState,
    { menuApi }: ThunkExtraArgument
  ) => {
    return menuApi
      .setMenuSection(menuSection)
      .then(() => {
        dispatch({ type: "SET_MENU_SECTION", payload: menuSection });
        return true;
      })
      .catch((error) => {
        addError(dispatch)(error);
        return false;
      });
  };
};

export const deleteMenuSection: (
  restaurantUuid: string,
  menuId: string,
  menuSectionId: string
) => ThunkActionReturn = (
  restaurantUuid: string,
  menuId: string,
  menuSectionId: string
) => {
  return (
    dispatch: MenuThunkDispatch,
    getState: () => ReduxState,
    { menuApi }: ThunkExtraArgument
  ) => {
    return menuApi
      .deleteMenuSection(restaurantUuid, menuId, menuSectionId)
      .then(() => {
        dispatch({
          type: "DELETE_MENU_SECTION",
          payload: { menuId, menuSectionId },
        });
      })
      .catch(addError(dispatch));
  };
};
